/**
 * Content Serialization and Hydration Utilities
 *
 * Ensures Tiptap's in-memory JSON AST is the absolute source of truth
 * across all storage, sync, and export pipelines.
 */

import { withoutTypedFrontmatter } from '../services/importExport/frontmatterText'

export interface TiptapDocJSON {
  type: 'doc'
  content?: any[]
  [key: string]: any
}

/**
 * Checks if a value is a valid Tiptap/ProseMirror JSON document AST
 */
export function isTiptapJsonDoc(data: unknown): data is TiptapDocJSON {
  if (typeof data !== 'object' || data === null) return false
  const doc = data as Record<string, any>
  return doc.type === 'doc' && (doc.content === undefined || Array.isArray(doc.content))
}

/**
 * Safely tries to parse a JSON string into a Tiptap AST or returns null
 */
export function tryParseTiptapJson(content: string): TiptapDocJSON | null {
  if (typeof content !== 'string') return null
  const trimmed = content.trim()
  if (!trimmed.startsWith('{') || !trimmed.endsWith('}')) return null
  try {
    const parsed = JSON.parse(trimmed)
    if (isTiptapJsonDoc(parsed)) {
      return parsed
    }
  } catch {
    // Not valid JSON
  }
  return null
}

/**
 * Hydrates the Tiptap editor directly from a JSON AST (object or string)
 */
export function setEditorContent(
  editor: any,
  content: string | object | null | undefined,
  options: { emitUpdate?: boolean } = {}
): void {
  if (!editor || !editor.commands) return
  // emitUpdate: false loads content without an editor update, so autosave
  // doesn't take it for the writer's typing.
  const emitUpdate = options.emitUpdate ?? true

  // Empty content handler
  if (content === null || content === undefined || content === '') {
    editor.commands.setContent('', { emitUpdate })
    return
  }

  // 1. Direct JSON AST object
  if (typeof content === 'object') {
    if (isTiptapJsonDoc(content)) {
      editor.commands.setContent(content, { emitUpdate })
      return
    }
    // Generic object -> setContent directly handles ProseMirror JSON
    editor.commands.setContent(content, { emitUpdate })
    return
  }

  // 2. JSON string
  if (typeof content === 'string') {
    const jsonAst = tryParseTiptapJson(content)
    if (jsonAst) {
      editor.commands.setContent(jsonAst, { emitUpdate })
      return
    }
    try {
      editor.commands.setContent(JSON.parse(content), { emitUpdate })
    } catch {
      console.warn('[contentUtils] Ignored page content that is not a JSON document')
      editor.commands.setContent('', { emitUpdate })
    }
  }
}

/**
 * Serializes the live in-memory Tiptap state as the JSON AST pages are stored in
 */
export function serializeEditorContent(editor: any): string {
  if (!editor) return ''
  return JSON.stringify(editor.getJSON(), null, 2)
}

/**
 * Removes @tiptap/markdown's &nbsp; blank-line markers and strips font-family
 * styling/tags so the exported Markdown is clean and compatible with standard
 * Markdown viewers (like Obsidian). Save keeps font tags for round-trip fidelity,
 * while Export produces clean Markdown without font tags.
 */
export function cleanMarkdownForExport(markdown: string): string {
  let result = markdown.replace(/^&nbsp;\s*$/gm, '')

  // Strip styling span tags and font tags while preserving their inner content
  const spanStyleRegex = /<span\b[^>]*?(?:font-family|font-size|color|data-font|data-text)[^>]*>([\s\S]*?)<\/span>/gi
  const fontTagRegex = /<font\b[^>]*>([\s\S]*?)<\/font>/gi

  let previous: string
  do {
    previous = result
    result = result.replace(spanStyleRegex, '$1')
    result = result.replace(fontTagRegex, '$1')
  } while (result !== previous)

  // Clean up any remaining stray font or styling tags if unclosed
  result = result.replace(/<\/?span\b[^>]*?(?:font-family|font-size|color|data-font|data-text)[^>]*>/gi, '')
  result = result.replace(/<\/?font\b[^>]*>/gi, '')

  return result
}

/**
 * Counts the number of words in a document content string.
 * Handles both ProseMirror JSON AST strings and plain Markdown / text.
 */
export function countWordsFromContent(content: string | object | null | undefined): number {
  if (!content) return 0
  const str = typeof content === 'string' ? content : JSON.stringify(content)
  try {
    const json = JSON.parse(str)
    // Front matter typed at the top of a page is metadata, not manuscript.
    const parsed = isTiptapJsonDoc(json) ? withoutTypedFrontmatter(json) : json
    let extractedText = ''
    function traverse(node: any) {
      if (!node) return
      if (node.text) extractedText += ' ' + node.text
      if (Array.isArray(node.content)) {
        for (const child of node.content) traverse(child)
      }
    }
    traverse(parsed)
    const trimmed = extractedText.trim()
    return trimmed.length === 0 ? 0 : trimmed.split(/\s+/).length
  } catch {
    const clean = str.replace(/[#*`_~\[\]()\-<>]/g, ' ')
    const trimmed = clean.trim()
    return trimmed.length === 0 ? 0 : trimmed.split(/\s+/).length
  }
}

/**
 * Whether an editor's document has actual user content — real text, or a
 * content-bearing leaf node like an image — as opposed to structural-only
 * changes like the empty paragraphs inserted by click-to-type or the
 * down-arrow-new-line shortcut. Used to gate promoting a lazily-created
 * page from "unsaved draft" to "persisted," so clicking or arrowing
 * around a blank startup page doesn't count as writing to it.
 */
export function hasMeaningfulContent(editor: any): boolean {
  if (!editor) return false
    if (editor.state.doc.textContent.trim().length > 0) return true
      let hasLeaf = false
      editor.state.doc.descendants((node: any) => {
        if (!node.isText && node.isLeaf) hasLeaf = true
      })
      return hasLeaf
}
